import { type BusinessDayResource, BusinessDaysController_list_v1 } from '@repo/api-client';
import { Alert, AlertDescription } from '@repo/ui/components/alert';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router';

import { useApiClient } from '@/api/api-client';
import { collectPages } from '@/api/collect-pages';
import { useSession } from '@/auth/session';
import { type Clock, systemClock } from '@/lib/clock';

import { decideAccess } from '../access';
import { paths } from '../paths';
import { addDays, currentBusinessDate } from '../reporting/reporting-period';
import { formatBusinessDate } from '../tickets/business-date';
import { dayCloseAccess } from './access';

/**
 * What a business-day row says about trading (blossom-rest-api #550). A row from
 * an API that has not deployed #550 carries neither field and counts as trading,
 * as every row did before: hiding the whole backlog would be the worse mistake.
 */
type DayRow = Pick<BusinessDayResource, 'state' | 'businessDate' | 'outletId'> &
  Partial<Pick<BusinessDayResource, 'salesCount' | 'hasOtherRecords'>>;

/** Ordered so that a date takes the busiest kind among its outlets' rows. */
const KINDS = ['empty', 'quiet', 'trading'] as const;
type Kind = (typeof KINDS)[number];

function kindOf(day: DayRow): Kind {
  if (day.salesCount === undefined || day.hasOtherRecords === undefined || day.salesCount > 0) {
    return 'trading';
  }

  return day.hasOtherRecords ? 'quiet' : 'empty';
}

/** The warning up to its link, and a quieter line after it; `null` draws no banner. */
interface Warning {
  lead: string;
  also: string | null;
}

function openDaysWarning(days: readonly DayRow[], today: string): Warning | null {
  const byDate = new Map<string, { kind: Kind; outlets: Set<string> }>();

  // `today` is excluded here as well as by the query's `to`: today's open day is
  // still trading and is never a reason to warn.
  for (const day of days) {
    if (day.state !== 'OPEN' || day.businessDate >= today) continue;

    const date = byDate.get(day.businessDate) ?? { kind: 'empty', outlets: new Set<string>() };
    const kind = kindOf(day);

    if (KINDS.indexOf(kind) > KINDS.indexOf(date.kind)) date.kind = kind;
    date.outlets.add(day.outletId);
    byDate.set(day.businessDate, date);
  }

  // `YYYY-MM-DD` sorts as text in date order. Empty days are never mentioned.
  const dates = (kind: Kind) =>
    [...byDate.entries()]
      .filter(([, date]) => date.kind === kind)
      .map(([d]) => d)
      .sort();
  const trading = dates('trading');
  const quiet = dates('quiet');
  const [oldestTrading] = trading;
  const [oldestQuiet] = quiet;

  if (oldestTrading !== undefined) {
    const outlets = byDate.get(oldestTrading)?.outlets.size ?? 0;
    const lead =
      trading.length > 1
        ? `${String(trading.length)} trading days are still open, the oldest ${formatBusinessDate(oldestTrading)}. Close them in`
        : `${formatBusinessDate(oldestTrading)} is still open at ${String(outlets)} ${outlets === 1 ? 'outlet' : 'outlets'}. Close it in`;

    let also: string | null = null;
    if (oldestQuiet !== undefined) {
      also =
        quiet.length > 1
          ? `Also open: ${String(quiet.length)} days with no sales but other records, the oldest ${formatBusinessDate(oldestQuiet)}.`
          : `Also open: ${formatBusinessDate(oldestQuiet)}, with no sales but other records.`;
    }

    return { lead, also };
  }

  if (oldestQuiet === undefined) return null;

  return {
    lead:
      quiet.length > 1
        ? `${String(quiet.length)} days with no sales but other records are still open, the oldest ${formatBusinessDate(oldestQuiet)}. Close them in`
        : `${formatBusinessDate(oldestQuiet)} is still open, with no sales but other records. Close it in`,
    also: null,
  };
}

/**
 * A line across the top of every back-office screen while a trading day before
 * today is still open anywhere the signed-in person can see (#335). A day with
 * no sales but other records is mentioned quietly; an empty one not at all (#349).
 *
 * Closing a day needs a counted drawer, and the longer it stays open the less
 * anyone can count — so a day nobody noticed is the failure this exists to end.
 *
 * Shown to exactly the people Day close admits, decided by that route's own
 * declaration, and not even asked for by anyone else. Re-read on navigation,
 * not on a timer, like `RefusedWritesLink`. A failed read draws nothing and is
 * logged: an alarm about the alarm is worse than the missing line.
 */
export function OpenDaysWarning({ clock = systemClock }: { clock?: Clock }) {
  const client = useApiClient();
  const { session } = useSession();
  const { pathname } = useLocation();
  const [warning, setWarning] = useState<Warning | null>(null);

  const admitted =
    decideAccess(
      dayCloseAccess,
      session === null
        ? null
        : { role: session.user.role, mustChangeCredential: session.user.mustChangeCredential },
    ) === 'render';

  useEffect(() => {
    if (!admitted) return;

    const controller = new AbortController();
    const today = currentBusinessDate(clock);

    // `collectPages` reads every page, so a long-unnoticed backlog is counted
    // whole rather than stopping at the route's default fifty rows.
    collectPages<BusinessDayResource>((offset, limit) =>
      client.request(BusinessDaysController_list_v1, {
        query: { state: 'OPEN', to: addDays(today, -1), offset, limit },
        signal: controller.signal,
      }),
    )
      .then((days) => {
        if (!controller.signal.aborted) setWarning(openDaysWarning(days, today));
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;

        console.error('day close: the open business days could not be read', error);
        setWarning(null);
      });

    return () => {
      controller.abort();
    };
  }, [admitted, client, clock, pathname]);

  if (!admitted || warning === null) return null;

  return (
    <div className="shrink-0 px-4 pt-3 lg:px-7">
      <Alert>
        <IconAlertTriangle />
        <AlertDescription>
          <p>
            {warning.lead}{' '}
            <Link to={paths.dayClose} className="font-medium underline underline-offset-3">
              Day close
            </Link>
            .
            {warning.also !== null && (
              <span className="text-muted-foreground/75"> {warning.also}</span>
            )}
          </p>
        </AlertDescription>
      </Alert>
    </div>
  );
}
